import { useEffect, useState } from "react";
import { PlusIcon, UploadIcon } from "@heroicons/react/outline";

import { FileStoreModalProps } from "./types";
import { StoresList } from "./StoresList";
import { FilesList } from "./FilesList";
import { FilePreviewDialog } from "./FilePreviewDialog";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDropzone } from "@/components/ui/FileDisplay/FileDisplayContent/AddFilePopover/FileDropzone";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useVectorStoreFileContentUrl } from "@/queries/vector-store";
import { displayErrorToast } from "@/utils/toast";

const getErrorMessage = (err: unknown, defaultMessage: string): string => {
  if (typeof err === "string") {
    return err;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return defaultMessage;
};

export const FileStoreModal: React.FC<FileStoreModalProps> = ({
  open,
  onClose,
  onSave,
  saveDisabled,
  title,
  storeListTitle,
  emptyStoresMessage,
  emptyFilesMessage,
  selectStoreMessage,
  filesHeaderLabel,

  isCreating,
  setIsCreating,
  newStoreName,
  setNewStoreName,
  isCreatingStore,
  handleCreateStore,
  handleCancelCreation,

  stores,
  isLoadingStores,
  activeStoreId,
  setActiveStoreId,
  isStoreSelected,
  toggleStoreSelection,
  onDeleteStore,
  isDeletingStore,

  files,
  isLoadingFiles,
  uploadingFiles,

  selectedFiles,
  isDragOver,
  isUploading,
  handleFileSelect,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleRemoveFile,
  handleUploadAndAttach,
  acceptedFormats,
  uploadHelperText,

  onDeleteFile,
  isFileDeleting,
  onPreviewFile,

  renderStoreInfo,
}) => {
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState("");

  const {
    previewUrl: previewBlobUrl,
    isLoading: isLoadingPreview,
    error: previewError,
  } = useVectorStoreFileContentUrl(previewFileId, previewFileName);

  useEffect(() => {
    if (previewError) {
      displayErrorToast(getErrorMessage(previewError, "Failed to fetch file preview"));
    }
  }, [previewError]);

  const handlePreviewFile = onPreviewFile
    ? onPreviewFile
    : (file: { previewId?: string; previewName?: string }) => {
        if (file.previewId && file.previewName) {
          setPreviewFileId(file.previewId);
          setPreviewFileName(file.previewName);
        }
      };

  const handleClosePreview = () => {
    setPreviewFileId(null);
    setPreviewFileName("");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[80vh] max-w-5xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* Left Panel - Stores List */}
          <div className="flex w-1/3 flex-col border-r pr-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold">{storeListTitle}</h3>
              <Button size="sm" variant="outline" onClick={() => setIsCreating(!isCreating)}>
                <PlusIcon className="mr-1 h-4 w-4" />
                Create
              </Button>
            </div>

            {isCreating && (
              <div className="mb-4 rounded-lg border bg-gray-50 p-3">
                <Label htmlFor="store-name" className="text-sm">
                  Store Name
                </Label>
                <Input
                  id="store-name"
                  value={newStoreName}
                  onChange={e => setNewStoreName(e.target.value)}
                  placeholder="Enter store name"
                  className="mb-2 mt-1"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateStore} disabled={isCreatingStore}>
                    {isCreatingStore ? <LoadingSpinner size={4} /> : "Create"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelCreation}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="flex-1 space-y-2 overflow-y-auto">
              <StoresList
                isLoading={isLoadingStores}
                stores={stores}
                activeStoreId={activeStoreId}
                onSelectStore={setActiveStoreId}
                isStoreSelected={isStoreSelected}
                toggleStoreSelection={toggleStoreSelection}
                onDeleteStore={onDeleteStore}
                isDeletingStore={isDeletingStore}
                emptyMessage={emptyStoresMessage}
              />
            </div>
          </div>

          {/* Right Panel - Store Details */}
          <div className="flex min-h-0 flex-1 flex-col">
            {activeStoreId ? (
              <>
                {renderStoreInfo && (
                  <div className="mb-4 flex-shrink-0">{renderStoreInfo(activeStoreId)}</div>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto">
                  <div className="mb-4">
                    <Label className="mb-2 block text-sm">Upload Files</Label>
                    <FileDropzone
                      isDragOver={isDragOver}
                      isUploading={false}
                      selectedFiles={selectedFiles}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onFileInputChange={handleFileSelect}
                      onRemoveFileAtIndex={handleRemoveFile}
                      acceptedFormats={acceptedFormats}
                      multiple
                      helperText={uploadHelperText}
                    />
                    {selectedFiles.length > 0 && (
                      <Button
                        className="mt-3 w-full"
                        onClick={handleUploadAndAttach}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <LoadingSpinner size={4} />
                        ) : (
                          <>
                            <UploadIcon className="mr-2 h-4 w-4" />
                            Upload {selectedFiles.length} File
                            {selectedFiles.length > 1 ? "s" : ""}
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  <FilesList
                    isLoading={isLoadingFiles}
                    files={files}
                    uploadingFiles={uploadingFiles}
                    emptyMessage={emptyFilesMessage}
                    headerLabel={filesHeaderLabel(files.length + uploadingFiles.length)}
                    onDeleteFile={onDeleteFile}
                    isFileDeleting={isFileDeleting}
                    onPreviewFile={handlePreviewFile}
                  />
                </div>
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                {selectStoreMessage}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saveDisabled}>
            Save Selection
          </Button>
        </div>
      </DialogContent>

      <FilePreviewDialog
        previewFileId={previewFileId}
        previewFileName={previewFileName}
        previewBlobUrl={previewBlobUrl ?? null}
        isLoadingPreview={isLoadingPreview}
        onClose={handleClosePreview}
      />
    </Dialog>
  );
};
